import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { get, isNil } from 'lodash';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AccountSettingKeys,
  createAccountSettingsMap,
} from '../auth/models/account-settings.model';
import { UserProfileModel } from '../auth/models/user-profile.model';
import { OrganizationAttributeModel } from '../organizations/models/organization-attribute.model';
import { OrganizationModel } from '../organizations/models/organization.model';
import { OrganizationsService } from '../organizations/organizations.service';
import { PendoConfiguration } from '../pendo.module';

declare const pendo: any;

export enum PendoEvents {
  DiscoveredChapterNav = 'Discovered Chapter Nav',
}

export interface PendoIdentityData {
  visitor: PendoVisitorData;
  account: PendoAccountData;
}

export interface PendoVisitorData {
  id?: string;
  email?: string;
  full_name?: string;
  purpose?: string;
  role?: string;
  field?: string;
  premium?: boolean;
  developer?: boolean;
  registration_date?: string;
  subscriptionType?: string;
  thirdParty?: boolean;
  teamRole?: string;
}

export interface PendoAccountData {
  id?: string;
  name?: string;
  subscriptionType?: string;
  subscriptionEndDate?: string;
  usersCount?: number;
  purpose?: string;
  field?: string;
  companyWebsite?: string;
  companySize?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PendoService {
  private appended = false;
  private initialized = false;
  private sessionEvents: { [key: string]: boolean } = {};
  private pageEvents: { [key: string]: boolean } = {};

  private queuedEvents = [];

  constructor(
    @Inject('pendoConfig') private config: PendoConfiguration,
    private router: Router,
    private route: ActivatedRoute,
    private organizationsService: OrganizationsService,
  ) {
    this.initRouteListener();
  }

  appendScript() {
    if (!this.config.enabled || this.appended) {
      return;
    }

    const script = `
      (function(apiKey){
        (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
        v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
            o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
            y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
            z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
      })('${this.config.pendoId}');
    `;

    const scriptElement = document.createElement('script');
    scriptElement.textContent = script;

    document.body.appendChild(scriptElement);

    this.appended = true;
  }

  initialize(identityData: PendoIdentityData) {
    if (!this.pendoEnabled()) {
      return;
    }

    const _identityData = { ...identityData };

    if (_identityData.visitor.id) {
      // Allow anonymous visitors
      this.setIds(_identityData);
    }

    pendo.initialize(_identityData);
    this.initialized = true;

    this.flushQueuedEvents();
  }

  identify(identityData: PendoIdentityData) {
    if (!this.pendoEnabled()) {
      return;
    }

    const _identityData = { ...identityData };
    this.setIds(_identityData);

    pendo.identify(_identityData);
  }

  // Function needs to handle a partial of UserProfileModel and return a partial of PendoVisitorData
  createVisitorData(userProfile: Partial<UserProfileModel>): Partial<PendoVisitorData> {
    const visitorData: PendoVisitorData = {};

    const accountSettings = createAccountSettingsMap(userProfile.account_settings);

    const teamRole = get(userProfile, 'organizations[0].user_role.role_display_name', null);

    if (userProfile.hasOwnProperty('id')) {
      visitorData.id = userProfile.id.toString();
    }

    if (userProfile.hasOwnProperty('email')) {
      visitorData.email = userProfile.email;
    }

    if (userProfile.hasOwnProperty('first_name') && userProfile.hasOwnProperty('last_name')) {
      visitorData.full_name = `${userProfile.first_name} ${userProfile.last_name}`;
    }

    if (userProfile.hasOwnProperty('registration_date')) {
      visitorData.registration_date = userProfile.registration_date;
    }

    if (userProfile.hasOwnProperty('premium_account')) {
      visitorData.premium = userProfile.premium_account;
    }

    if (userProfile.hasOwnProperty('user_is_developer')) {
      visitorData.developer = userProfile.user_is_developer;
    }

    if (userProfile.hasOwnProperty('subscription')) {
      visitorData.subscriptionType = userProfile.subscription
        ? userProfile.subscription.subscription_type
        : null;
    }

    if (userProfile.hasOwnProperty('is_third_party')) {
      visitorData.thirdParty = userProfile.is_third_party;
    }

    // Account Settings

    if (accountSettings.hasOwnProperty('account_purpose')) {
      visitorData.purpose = accountSettings.account_purpose
        ? accountSettings.account_purpose.value
        : null;
    }

    if (accountSettings.hasOwnProperty('account_role')) {
      visitorData.role = accountSettings.account_role ? accountSettings.account_role.value : null;
    }

    if (accountSettings.hasOwnProperty('account_field')) {
      visitorData.field = accountSettings.account_field
        ? accountSettings.account_field.value
        : null;
    }

    if (teamRole) {
      visitorData.teamRole = teamRole;
    }

    return visitorData;
  }

  updateVisitorData(userProfile: Partial<UserProfileModel>) {
    if (!this.pendoEnabled()) {
      return;
    }

    pendo.updateOptions({ visitor: this.createVisitorData(userProfile) });
  }

  // Uses first organization in userProfile.organizations array
  createAccountData(organization: OrganizationModel): PendoAccountData {
    const groupAttributes = get(organization, 'group_attributes', null);

    return {
      id: get(organization, 'id', null),
      name: get(organization, 'name', null),
      subscriptionType: get(organization, 'subscription.type', null),
      subscriptionEndDate: get(organization, 'subscription.paid_through_date', null),
      usersCount: get(organization, 'users_count', null),
      purpose: this.getGroupAttributeValue(groupAttributes, 'account_purpose'),
      field: this.getGroupAttributeValue(groupAttributes, 'account_field'),
      companyWebsite: this.getGroupAttributeValue(groupAttributes, 'account_company_website'),
      companySize: this.getGroupAttributeValue(groupAttributes, 'account_company_size'),
    };
  }

  flushQueuedEvents() {
    this.queuedEvents.forEach((event) => {
      this.track(event.trackType, event.metadata);
    });
  }

  track(trackType: string, metadata?: any) {
    if (this.pendoEnabled()) {
      if (this.initialized) {
        pendo.track(trackType, metadata);
      } else {
        this.queuedEvents.push({ trackType, metadata });
      }
    }
  }

  trackOncePerSession(trackType: string, metadata?: any) {
    // If the event has already been tracked, don't track again
    if (this.sessionEvents[trackType]) {
      return;
    }

    if (this.pendoEnabled()) {
      this.track(trackType, metadata);
      this.sessionEvents[trackType] = true;
    }
  }

  trackOncePerPage(trackType: string, metadata?: any) {
    // If the event has already been tracked, don't track again
    if (this.pageEvents[trackType]) {
      return;
    }

    if (this.pendoEnabled()) {
      this.track(trackType, metadata);
      this.pageEvents[trackType] = true;
    }
  }

  trackSignUp(type: string) {
    const queryParams = this.route.snapshot.queryParams;

    this.track('Sign Up - Success', {
      type: type,
      referrer: document.referrer,
      refParam: queryParams.ref || null,
      groupInvite: queryParams.hasOwnProperty('group_invite'),
      groupCode: queryParams.hasOwnProperty('group') && queryParams.hasOwnProperty('code'),
    });
  }

  private initRouteListener() {
    // On any page change, reset the page events logger so that trackOncePerPage works properly
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.pageEvents = {};
    });
  }

  private pendoEnabled(): boolean {
    if (!this.config.enabled) {
      return false;
    }

    // Limit pendo to specific pages so that we don't blow through our data budget
    // Mostly used by viewer
    const whitelist: string[] = get(this.config, `whitelist[${environment.application}]`, null);
    if (!isNil(whitelist)) {
      // Normally we'd use activatedRoute, but its not initalized at this point, so
      // we're falling back to native javascript
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get('id') || urlParams.get('be') || urlParams.get('m');

      return whitelist.includes(id);
    }

    return true;
  }

  // Assign a unique prefix to visitor ID for different deployment environments
  // So that there are no conflicts between local, QA, staging, and production data
  private setIds(identityData: PendoIdentityData) {
    const prefix = get(this.config, `idPrefixes[${environment.deploy}]`, '');

    identityData.visitor.id = `${prefix}${identityData.visitor.id}`;
    identityData.account.id = `${prefix}${identityData.account.id ?? 999999999}`;
  }

  private getGroupAttributeValue(
    attributes: OrganizationAttributeModel[],
    attributeName: AccountSettingKeys,
  ): string | null {
    if (!attributes) {
      return null;
    }

    const attribute = this.organizationsService.getOrganizationAttribute(
      attributes,
      'account_settings',
      attributeName,
    );

    return attribute ? attribute.value : null;
  }
}
